// Override Turbo's default confirm dialog (browser native `confirm()`) with
// a styled modal using the existing `.modal-overlay` / `.modal` / `.modal__*`
// classes from app/assets/stylesheets/components/_modal.css.
//
// Every `data: { turbo_confirm: "..." }` site in the codebase routes through
// this — no markup changes at call sites.
import { Turbo } from "@hotwired/turbo-rails"

interface TurboConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
}

function parseMessage(raw: string): { message: string; options: TurboConfirmOptions } {
  // Allow callers to pass JSON for customization, falling back to plain text.
  // Example: data-turbo-confirm='{"message":"...","confirmLabel":"Yes, cancel"}'
  if (raw.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(raw)
      return {
        message: parsed.message || "Are you sure?",
        options: {
          title: parsed.title,
          confirmLabel: parsed.confirmLabel,
          cancelLabel: parsed.cancelLabel
        }
      }
    } catch {
      // Fall through to plain-text handling
    }
  }
  return { message: raw, options: {} }
}

function showConfirmModal(message: string, options: TurboConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div")
    overlay.className = "modal-overlay"
    overlay.innerHTML = `
      <div class="modal modal--confirm" role="dialog" aria-modal="true">
        <div class="modal__header">
          <h2 class="modal__title"></h2>
          <button type="button" class="modal__close" aria-label="Close">&times;</button>
        </div>
        <div class="modal__body">
          <p></p>
        </div>
        <div class="modal__footer">
          <button type="button" class="btn btn-secondary" data-action="cancel"></button>
          <button type="button" class="btn btn-danger" data-action="confirm"></button>
        </div>
      </div>
    `

    // textContent (not innerHTML) for user-supplied strings to avoid XSS.
    overlay.querySelector(".modal__title")!.textContent = options.title || "Please confirm"
    overlay.querySelector(".modal__body p")!.textContent = message
    overlay.querySelector('[data-action="confirm"]')!.textContent = options.confirmLabel || "Confirm"
    overlay.querySelector('[data-action="cancel"]')!.textContent = options.cancelLabel || "Cancel"

    const cleanup = (result: boolean) => {
      document.removeEventListener("keydown", onKeydown)
      overlay.remove()
      document.body.style.overflow = ""
      resolve(result)
    }

    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === "Escape") cleanup(false)
      if (e.key === "Enter") cleanup(true)
    }

    overlay.addEventListener("click", (e) => {
      const target = e.target as HTMLElement
      if (target === overlay) return cleanup(false) // backdrop click
      if (target.closest('[data-action="cancel"], .modal__close')) return cleanup(false)
      if (target.closest('[data-action="confirm"]')) return cleanup(true)
    })

    document.addEventListener("keydown", onKeydown)
    document.body.style.overflow = "hidden"
    document.body.appendChild(overlay)

    // Focus the confirm button by default — Enter immediately confirms.
    ;(overlay.querySelector('[data-action="confirm"]') as HTMLButtonElement)?.focus()
  })
}

Turbo.setConfirmMethod((message: string) => {
  const { message: text, options } = parseMessage(message)
  return showConfirmModal(text, options)
})
